import React from "react";
import { motion } from "framer-motion";
import {
  ChevronDown,
  MapPin,
  Phone,
  Mail,
  Clock,
  Utensils,
  Flame,
  Star,
  ShoppingCart,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AppName, Packs, Logo } from "../../lib/data";

function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 border-b border-red-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex items-center justify-center">
                <img src={Logo} alt={AppName} className="w-8 h-8 rounded" />
              </div>
              <span className="text-xl font-bold text-gray-800">{AppName}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden md:flex space-x-8"
            >
              <a
                href="#home"
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                Home
              </a>
              <a
                href="#about"
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                About
              </a>
              <a
                href="#menu"
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                Menu
              </a>
              <a
                href="#locations"
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                Locations
              </a>
              <a
                href="#stores"
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                Stores
              </a>
              <a
                href="#contact"
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                Contact
              </a>
              <Link
                to="/order"
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                Order Now
              </Link>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20"
      >
        {/* Enhanced background with food imagery */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-600/10 via-orange-500/10 to-yellow-500/10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <div className="w-24 h-24 bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                <img
                  src={Logo}
                  alt={AppName}
                  className="w-16 h-16 rounded-full"
                />
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-7xl font-bold text-gray-800 mb-6"
            >
              Welcome to{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                Uncle Stans Foods
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Experience the authentic taste of Nigerian Small Chops & Barbecue
              Delights, crafted with love and traditional recipes passed down
              through generations
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button
                onClick={() => {
                  const menuSection = document.getElementById("menu");
                  if (menuSection) {
                    menuSection.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="w-[200px] bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105"
              >
                View Our Menu
              </button>
              <Link
                to="/order"
                className="w-[200px] border-2 border-red-600 text-red-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-red-600 hover:text-white transition-all duration-300"
              >
                Order Now
              </Link>
            </motion.div>

            {/* Food highlights */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto"
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Fresh BBQ
                </span>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Utensils className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Small Chops
                </span>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Premium Quality
                </span>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Fast Delivery
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-red-600 animate-bounce" />
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              About <span className="text-red-600">Uncle Stans</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We bring you the finest small chops and barbecue experiences,
              crafted with love and tradition.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Our Story
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Uncle Stans Foods has been serving authentic Nigerian small
                chops and barbecue delights for over a decade. Our recipes have
                been passed down through generations, ensuring every bite
                carries the rich flavors of tradition.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We take pride in using only the freshest ingredients and
                traditional cooking methods to bring you the most authentic
                taste of home-cooked goodness.
              </p>

              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Flame className="w-8 h-8 text-red-600" />
                  </div>
                  <h4 className="font-semibold text-gray-800">Fresh BBQ</h4>
                  <p className="text-sm text-gray-600">Made fresh daily</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Utensils className="w-8 h-8 text-red-600" />
                  </div>
                  <h4 className="font-semibold text-gray-800">Small Chops</h4>
                  <p className="text-sm text-gray-600">Traditional recipes</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Why Choose Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Authentic traditional recipes</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Fresh, quality ingredients</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Family-owned business</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span>Decades of experience</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section
        id="menu"
        className="py-20 bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Our{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                Menu Packs
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our carefully curated packs of authentic Nigerian
              small chops and barbecue delights, each crafted with love and
              tradition
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Packs.map((pack, index) => (
              <motion.div
                key={pack.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden flex flex-col border border-orange-100"
              >
                {pack.name === "Premium Pack" && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1 shadow-lg">
                    <Star className="w-4 h-4" />
                    <span>Popular</span>
                  </div>
                )}

                <div className="p-8 flex flex-col flex-1">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      {pack.name}
                    </h3>
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 mb-4">
                      ₦{pack.price.toLocaleString()}
                    </div>
                    <div className="w-16 h-1 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 mx-auto rounded-full"></div>
                  </div>

                  <div className="space-y-3 mb-8 flex-1">
                    {pack.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="flex items-center justify-between py-2 border-b border-orange-100 last:border-b-0 hover:bg-orange-50 rounded-lg px-2 transition-colors"
                      >
                        <span className="text-gray-700 font-medium">
                          {item.name}
                        </span>
                        <span className="text-red-600 font-bold text-lg">
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/order"
                    className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-3 px-6 rounded-full font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center space-x-2 group mt-auto shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <ShoppingCart className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    <span>Order Now</span>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-gray-600 mb-6">
              All packs include our signature dipping sauces and are perfect for
              sharing with family and friends
            </p>
            <Link
              to="/order"
              className="border-2 border-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gradient-to-r hover:from-red-600 hover:via-orange-500 hover:to-yellow-500 hover:text-white transition-all duration-300"
            >
              View Full Menu
            </Link>
          </motion.div>
        </div>
      </section>
      {/* Stores Section */}
      <section
        id="stores"
        className="py-20 bg-gradient-to-br from-gray-50 to-gray-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Visit Our{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                Stores
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience our delicious small chops and barbecue in person at any
              of our convenient store locations across Lagos.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Opebi Store",
                address: "15/17 Opebi Road, Ikeja, Lagos",
                area: "Ikeja",
                description: "Convenient location in the heart of Ikeja",
                features: [
                  "Walk-in Orders",
                  "Fresh Daily",
                  "Parking Available",
                ],
              },
              {
                name: "Ikota Store",
                address:
                  "Road 5 Shop I-326, Ikota Shopping Complex VGC, Ajah, Lagos",
                area: "Ajah",
                description: "Located in the popular Ikota Shopping Complex",
                features: [
                  "Shopping Complex",
                  "Multiple Options",
                  "Easy Access",
                ],
              },
              {
                name: "Osapa Store",
                address:
                  "Super Saver Mall, Osapa London, Beside Shoprite, Lekki",
                area: "Lekki",
                description: "Next to Shoprite for your convenience",
                features: ["Mall Location", "Near Shoprite", "Ample Parking"],
              },
              {
                name: "Admiralty Store",
                address:
                  "Jubilee Mall, Admiralty Way, Lekki Phase 1, Lagos, Nigeria",
                area: "Lekki Phase 1",
                description: "Premium location in upscale Lekki Phase 1",
                features: ["Premium Location", "Mall Setting", "Upscale Area"],
              },
              {
                name: "Ikoyi Store",
                address: "Ikoyi Plaza, Awolowo Road, Ikoyi",
                area: "Ikoyi",
                description: "Exclusive location in prestigious Ikoyi",
                features: [
                  "Exclusive Area",
                  "Premium Service",
                  "Business District",
                ],
              },
            ].map((store, index) => (
              <motion.div
                key={store.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 overflow-hidden group flex flex-col"
              >
                {/* Store Header */}
                <div className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 p-6 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform duration-300"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold backdrop-blur-sm">
                        Walk-in
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{store.name}</h3>
                    <p className="text-white/90 font-medium">{store.area}</p>
                  </div>
                </div>

                {/* Store Content */}
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {store.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-red-500" />
                      Address
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {store.address}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="mb-6 flex-1">
                    <h4 className="font-semibold text-gray-800 mb-3">
                      Features
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {store.features.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className="bg-gradient-to-r from-red-50 to-orange-50 text-red-700 px-3 py-1 rounded-full text-xs font-medium border border-red-100"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-auto">
                    <button className="flex-1 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2">
                      <MapPin className="w-4 h-4" />
                      <span>View on Map</span>
                    </button>
                    <Link
                      to="/order"
                      className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      <span>Order Online</span>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 text-white shadow-xl">
              <h3 className="text-2xl font-bold mb-4">Prefer to Visit Us?</h3>
              <p className="text-lg mb-6 opacity-90">
                Walk into any of our stores for a fresh, hot meal prepared right
                in front of you. Experience our authentic flavors and warm
                hospitality in person.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    const storesSection = document.getElementById("stores");
                    if (storesSection) {
                      storesSection.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white px-8 py-3 rounded-full font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                >
                  <MapPin className="w-5 h-5" />
                  <span>View Stores</span>
                </button>
                <Link
                  to="/order"
                  className="bg-white text-gray-800 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span>Order for Delivery</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Locations Section */}
      <section id="locations" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Contact{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                Us
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Visit us at any of our convenient locations across Lagos. We're
              here to serve you the best small chops and barbecue delights.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Admiralty",
                phone: "0813-612-3468",
                area: "Lekki",
                description: "Premium location serving the Admiralty community",
              },
              {
                name: "Opebi",
                phone: "0909-666-6466",
                area: "Ikeja",
                description: "Convenient location in the heart of Ikeja",
              },
              {
                name: "Ikoyi",
                phone: "0818-888-8314",
                area: "Victoria Island",
                description: "Exclusive location in upscale Ikoyi",
              },
              {
                name: "Ikota",
                phone: "0818-888-8407",
                area: "Lekki",
                description: "Family-friendly location in Ikota",
              },
              {
                name: "Osapa",
                phone: "0901-600-0420",
                area: "Lekki",
                description: "Community-focused location in Osapa",
              },
              {
                name: "Hotline",
                phone: "0818-888-8603",
                area: "Lagos",
                description: "24/7 customer service hotline",
              },
            ].map((location, index) => (
              <motion.div
                key={location.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-orange-100 relative overflow-hidden group"
              >
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-600/10 via-orange-500/10 to-yellow-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform duration-300"></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    {location.name === "Hotline" && (
                      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span>24/7</span>
                      </div>
                    )}
                  </div>

                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {location.name}
                  </h3>
                  <p className="text-gray-600 mb-1 font-medium">
                    {location.area}
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    {location.description}
                  </p>

                  <div className="bg-white rounded-xl p-4 shadow-md border border-orange-100">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Call us at</p>
                        <a
                          href={`tel:${location.phone}`}
                          className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300"
                        >
                          {location.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <div className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 rounded-2xl p-8 text-white shadow-xl">
              <h3 className="text-2xl font-bold mb-4">Need Help?</h3>
              <p className="text-lg mb-6 opacity-90">
                Our customer service team is available 24/7 to assist you with
                orders, delivery inquiries, and any questions you may have.
              </p>
              <a
                href="tel:0818-888-8603"
                className="inline-flex items-center space-x-2 bg-white text-red-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                <Phone className="w-5 h-5" />
                <span>Call Hotline: 0818-888-8603</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex items-center justify-center">
                  <img src={Logo} alt={AppName} className="w-8 h-8 rounded" />
                </div>
                <span className="text-xl font-bold">{AppName}</span>
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Bringing you the finest small chops and barbecue experiences
                with authentic flavors and traditional recipes.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">F</span>
                </div>
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">I</span>
                </div>
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">T</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#home"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Home
                  </a>
                </li>
                <li>
                  <a
                    href="#about"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="#menu"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Menu
                  </a>
                </li>
                <li>
                  <a
                    href="#contact"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <Link
                    to="/order"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Order Now
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-red-400" />
                  <span className="text-gray-300">123 Food Street, Lagos</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-red-400" />
                  <span className="text-gray-300">+234 123 456 7890</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-red-400" />
                  <span className="text-gray-300">info@unclestans.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-red-400" />
                  <span className="text-gray-300">Mon-Sat: 9AM-10PM</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              © {new Date().getFullYear()} {AppName}. All rights reserved. Made
              with ❤️ for food lovers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
